import { assert } from '@ember/debug';
import { getOwner, setOwner } from '@ember/application';
import { task, rawTimeout } from 'ember-concurrency';
import hasEqualUrls from 'ember-stereo/-private/utils/has-equal-urls';
import normalizeIdentifier from 'ember-stereo/-private/utils/normalize-identifier';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { next } from '@ember/runloop';
import { sub } from 'date-fns';
import { numericDuration } from 'ember-stereo/helpers/numeric-duration';
import { registerDestructor } from '@ember/destroyable';

export default class Feed {
  @service store;
  @service socket;
  @service session;
  @service stereo;
  @service feeds;
  @service playback;
  @service clock;
  @service settings;

  @tracked airing;
  @tracked note;
  @tracked track;
  @tracked item;
  @tracked itemType;
  @tracked broadcast;
  @tracked station;
  @tracked show;
  @tracked isLoaded = false
  @tracked itemsLastFetchedAt;

  constructor(feedableObject, options = { }) {
    assert("owner is required to create a feed object", options.owner);
    try {
      let owner = getOwner(options.owner) || getOwner(feedableObject)
      setOwner(this, owner);
    } catch(e) {
      console.error(e)
      throw new Error('error while setting owner on feed object')
    }

    registerDestructor(this, () => {
      this.airingCableConnection?.unsubscribe();
    });
    this.feedObject = feedableObject;
  }

  get imageSource() {
    // given the airing, show, and station, find the image in that order
    if (this.airing?.image?.small) {
      return this.airing.image
    } else if (this.show?.image?.small) {
      return this.show.image
    } else if (this.station?.image?.small) {
      return this.station.image
    }
  }

  get startsAt() {
    return this.airing?.startsAt
  }

  get endsAt() {
    return this.airing?.endsAt
  }

  get stationId() {
    return this.station?.uid
  }

  get broadcastId() {
    return this.broadcast?.id
  }

  get loadedSound() {
    if (this.identifier) {
      return this.stereo.findSound(this.identifier);
    }
  }

  get hasItems() {
    return this.airing?.publicItemsCount > 0
  }

  get itemsFetched() {
    return !!this.itemsLastFetchedAt;
  }

  get itemsLoading() {
    return this.loadAiringItemsTask.isRunning;
  }

  get items() {
    if (!this.itemsFetched) {
      next(this, () => { 
        this.loadAiringItemsTask.perform()
      });
    }

    if (this.airing) {
      return _.compact([...this.airing.notes, ...this.airing.tracks]);;
    } else {
      return []
    }
  }

  playPauseTask = task({ restartable: true, maxConcurrency: 1 }, async (trackStartTime = 0) => {
    if (this.loadedSound?.isLoaded) {
      this.loadedSound.togglePause();
    } else {
      await this.playTask.linked().perform(trackStartTime);
    }
  });

  loadAiringItemsTask = task({drop: true}, async () => {
    while (!this.airing || !this.settings.isLoaded) {
      await rawTimeout(10);
    }

    let airing = await this.store.queryRecord('airing', {
      id: this.airing.id,
      include: ['tracks', 'notes'].join(','),
      reload: true
    })

    this.itemsLastFetchedAt = new Date();
    this.airing = airing;

    if (!this.airingCableConnection) {
      this.loadAiringUpdatesCableTask.perform();
    }
  });

  loadAiringUpdatesCableTask = task({ maxConcurrency: 1, restartable: true }, async () => {
    this.airingCableConnection = this.socket.subscribe(
      {
        channel: 'SignalChannel',
        station_id: this.station.id,
        airing_id: this.airing.id,
      },
      {
        received: (data) => {
          if (this.settings.debug) {
            console.log(
              '⚡️ received SignalChannel update for playback feed, handling...', data
            );
          }
          if (this.itemsFetched) { // if we've already loaded this items, that means we're using the items
            this.loadAiringItemsTask.perform()
          } else {
            this.airing.reload();
          }
          this.lastCacheKey = data?.cache;
        },
      }
    );
  });

  get normalizedIdentifier() { // This has query params and tokens stripped from it
    return normalizeIdentifier(this.url)
  }

  get identifier() {
    return this.url;
  }

  matches(params = {}) {
    if ('identifier' in params) {
      return hasEqualUrls(normalizeIdentifier(params.identifier), this.normalizedIdentifier)
    } else if ('url' in params) {
      return hasEqualUrls(normalizeIdentifier(params.url), this.normalizedIdentifier)
    } else if ('feedRecord' in params) {
      return params.feedRecord?.id === this.feedObject?.id
    } else if ('airing' in params) {
      return params.airing?.id === this.airing?.id
    } else if (('showId' in params) && ('episodeIndex' in params) && params.episodeIndex) {
      let matchingId = String(this.show?.id) === String(params.showId) || String(this.show?.slug) === String(params.showId);
      return matchingId && String(this.episodeIndex) === String(params.episodeIndex);
    } else if (('showId' in params)) {
      let matchingId = String(this.show?.id) === String(params.showId) || String(this.show?.slug) === String(params.showId);
      return matchingId && String(this.episodeIndex) === String(0);
    }
  }

  get currentPercentageElapsed() {
    if (this.isLive && this.airing) {
      let currentPosition = this.clock.date.getTime() - this.airing.startsAt.getTime();
      return (
        (currentPosition / (this.airing.endsAt.getTime() - this.airing.startsAt.getTime())) *
        100
      );
    } else if (
      this.airing &&
      this.clock.date >= this.airing.startsAt &&
      this.clock.date <= this.airing.endsAt &&
      this.loadedSound?.isSeekable
    ) {
      let currentPosition = this.clock.date.getTime() - this.airing.startsAt.getTime();

      return (
        (currentPosition / (this.airing.endsAt.getTime() - this.airing.startsAt.getTime())) *
        (this.loadedSound?.position / this.loadedSound?.duration) *
        100
      );
    } else if (this.loadedSound) {
      return (this.loadedSound?.position / this.loadedSound?.duration) * 100;
    } else {
      return 0;
    }
  }

  get timeRemaining() {
    if (this.isLive) {
      let remaining =
        new Date(this.airing.endsAt).getTime() -
        this.clock.date.getTime();
      return numericDuration([remaining]);
    } else if (this.loadedSound) {
      let remaining = this.loadedSound.duration - this.loadedSound.position;
      return numericDuration([remaining]);
    } else {
      return 0;
    }
  }

  get timeElapsed() {
    if (this.isLive && this.airing) {
      let elapsed =
        this.clock.date.getTime() -
        new Date(this.airing.startsAt).getTime();
      return numericDuration([elapsed]);
    } else if (this.loadedSound) {
      return numericDuration([this.loadedSound.position]);
    } else {
      return 0;
    }
  }
}
